import { useCallback, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { MDSText, MDSTextVariant, MDSTextWeight, MDSButtonVariant } from "@/mds";
import { useAppStore } from "@/store";
import { MODEL_OPTIONS, CHAT_MODEL_LABELS } from "@/domains/chat-model";
import { ComboboxDropdown } from "@/mds/components/forms/combobox/combobox-dropdown";
import { MDSComboboxDropdownConfig } from "@/mds/components/forms/combobox/combobox.types";
import { ChatModel } from "@/domains/chat-model";
import { MDSIconKind } from "@/mds/components/icon";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { GatedFeature } from "@/store/feature-gates/types";
import { PaywallContext } from "@/domains/metrics/tracked-event";

export const IntegrationsModelSelectionForChromeExtension = observer(() => {
  const { store } = useAppStore();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const isPaidUser = store.subscriptions?.featureAccess?.isPaid ?? false;

  const handleSelect = useCallback(
    (optionId: string) => {
      const selectedOption = MODEL_OPTIONS.find(opt => opt.id === optionId);
      const isPremiumModel = MODEL_OPTIONS.indexOf(selectedOption!) > 0;

      trackEvent(TrackedEvent.ChromeExtensionModelSelect, {
        selectedModel: optionId as ChatModel,
        previousModel: store.chromeExtension.selectedModelKind,
      });

      if (isPremiumModel && !isPaidUser) {
        store.subscriptions.openUsageLimitUpsell({
          feature: GatedFeature.ChatMessageSend,
          context: PaywallContext.ChatModelSelection,
        });
        setIsDropdownOpen(false);
        return;
      }

      store.chromeExtension.saveModelSelection(optionId as ChatModel);
      setIsDropdownOpen(false);
    },
    [store, isPaidUser]
  );

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setIsDropdownOpen(isOpen);

      if (isOpen) {
        trackEvent(TrackedEvent.ChromeExtensionModelDropdownOpen, {
          currentModel: store.chromeExtension.selectedModelKind,
        });
      }
    },
    [store]
  );

  const selectedModelKind = store.chromeExtension.selectedModelKind;
  const isLegacyModel = !MODEL_OPTIONS.some(opt => opt.id === selectedModelKind);

  const options = MODEL_OPTIONS.map((opt, index) => {
    const isPremiumModel = index > 0;
    const isSelected = opt.id === selectedModelKind;

    let showBadge = false;
    let badgeLabel: string | undefined;

    if (!isSelected && isPremiumModel && !isPaidUser) {
      showBadge = true;
      badgeLabel = "Pro";
    }

    return {
      ...opt,
      showBadge,
      badgeLabel,
    };
  });

  if (isLegacyModel) {
    options.unshift({
      id: selectedModelKind,
      label: CHAT_MODEL_LABELS[selectedModelKind] ?? "Unknown",
      icon: MDSIconKind.Anthropic,
      showBadge: false,
      badgeLabel: undefined,
    });
  }

  const dropdownConfig: MDSComboboxDropdownConfig = {
    options,
    selectedId: selectedModelKind,
    onSelect: handleSelect,
    dividerAfterIndex: isLegacyModel ? 1 : 0,
  };

  return (
    <StyledModelSelection>
      <StyledModelSelectionInfo>
        <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
          Model selection
        </MDSText>
        <MDSText variant={MDSTextVariant.ParagraphS}>
          Change the default model Chat uses when clipping websites
        </MDSText>
      </StyledModelSelectionInfo>
      <ComboboxDropdown
        config={dropdownConfig}
        isOpen={isDropdownOpen}
        onOpenChange={handleOpenChange}
        onSelect={handleSelect}
        buttonVariant={MDSButtonVariant.Default}
        isLoading={store.chromeExtension.isSavingModelSelection}
      />
    </StyledModelSelection>
  );
});

const StyledModelSelection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ theme }) => theme.colors.surface.pane};
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[3]}`};
  width: 100%;
  max-width: 784px;
`;

const StyledModelSelectionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;
